import React, { Component } from 'react';
import { Row, Col, Modal, Form, FormControl, Nav, NavItem, Checkbox } from 'react-bootstrap';
import { translate } from 'react-i18next';
import axios from 'axios';
import config from '../config';

/**
 * @name TemplateSnippetBackgroundPicker
 * @component Modal for choosing the background image from org libraries or template libraries
 */
export class TemplateSnippetBackgroundPicker extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedBackground: '',
            selectedLibrary: '',
            mediaLibraries: [],
            media: [],
            librarySource: 'org', // 'org' or 'template'
            filterTextOverlays: false,
        };
    }

    componentDidMount() {
        this.loadLibrariesFromServer();
    }

    /**
     * @name loadLibrariesFromServer()
     * @desc Load libraries based on current source (org or template)
     */
    loadLibrariesFromServer = () => {
        var self = this;
        const { librarySource } = this.state;

        if (librarySource === 'org') {
            // Load organization media libraries
            axios
                .get(
                    `${config.baseURL}/organisations/${
                    this.props.organisationUUID
                    }/medialibraries`
                )
                .then(function (response) {
                    self.setState({ mediaLibraries: response.data.mediaLibraries });
                    if (response.data.mediaLibraries.length > 0) {
                        self.setState(
                            {
                                selectedLibrary:
                                    response.data.mediaLibraries[0].uuid
                            },
                            () => {
                                self.loadMediaFromLibrary();
                            }
                        );
                    }
                })
                .catch(function (error) {
                    console.log(error);
                });
        } else {
            // Load template libraries
            axios
                .get(`${config.baseURL}/templatelibraries`, {
                    params: {
                        page: 1,
                        limit: 1000
                    }
                })
                .then(function (response) {
                    self.setState({ mediaLibraries: response.data.mediaLibraries });
                    if (response.data.mediaLibraries.length > 0) {
                        self.setState(
                            {
                                selectedLibrary:
                                    response.data.mediaLibraries[0].uuid
                            },
                            () => {
                                self.loadMediaFromLibrary();
                            }
                        );
                    }
                })
                .catch(function (error) {
                    console.log(error);
                });
        }
    };

    /**
     * @name loadMediaFromLibrary
     * @desc Load media from the currently selected library
     */
    loadMediaFromLibrary = () => {
        const { selectedLibrary, librarySource } = this.state;
        var self = this;

        const endpoint = librarySource === 'template'
            ? `${config.baseURL}/templatelibraries/${selectedLibrary}/media`
            : `${config.baseURL}/medialibraries/${selectedLibrary}/media`;

        axios
            .get(endpoint)
            .then(function (response) {
                self.setState({ media: response.data.media || [] });
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    /**
     * @name handleSelectLibrary
     * @desc Sets the selectLibrary state to the form value, gets media from the selected media library
     * @param Event
     */
    handleSelectLibrary = e => {
        this.setState(
            {
                selectedLibrary: e.target.value
            },
            () => {
                this.loadMediaFromLibrary();
            }
        );
    };

    /**
     * @name handleSourceChange
     * @desc Switch between org and template libraries
     */
    handleSourceChange = (source) => {
        this.setState(
            {
                librarySource: source,
                selectedLibrary: '',
                mediaLibraries: [],
                media: []
            },
            () => {
                this.loadLibrariesFromServer();
            }
        );
    };

    /**
     * @name toggleFilterTextOverlays
     * @desc Toggle filter for media with text overlays
     */
    toggleFilterTextOverlays = () => {
        this.setState({
            filterTextOverlays: !this.state.filterTextOverlays
        });
    };

    render() {
        const {
            modalShow,
            handleModalClose,
            handleModalSave,
            handleSelectBackground,
            t
        } = this.props;
        const { mediaLibraries, selectedLibrary, librarySource, filterTextOverlays, media } = this.state;
        const libraryMap = Array.from(mediaLibraries);

        let organisationUUID = this.props.organisationUUID;

        // Get the selected library object to determine orgUUID for template libraries
        const selectedLibraryObj = mediaLibraries.find(lib => lib.uuid === selectedLibrary);
        const displayOrgUUID = librarySource === 'template' && selectedLibraryObj
            ? selectedLibraryObj.organisationUUID
            : organisationUUID;

        // Filter media if needed
        let thumbs = media || [];
        if (filterTextOverlays && librarySource === 'template') {
            thumbs = thumbs.filter(m => m.hasTextOverlay === true);
        }

        let self = this;

        return (
            <Modal
                show={modalShow}
                onHide={handleModalClose}
                dialogClassName="modal-80"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('TemplateSnippet.modal_title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {/* Tab Navigation */}
                        <Nav bsStyle="tabs" activeKey={librarySource} onSelect={this.handleSourceChange} style={{ marginBottom: '20px' }}>
                            <NavItem eventKey="org">
                                {t('TemplateSnippetBackgroundPicker.tab_org_libraries')}
                            </NavItem>
                            <NavItem eventKey="template">
                                {t('TemplateSnippetBackgroundPicker.tab_template_libraries')}
                            </NavItem>
                        </Nav>

                        <Row id="modal-form-wrapper">
                            <Col id="modal-form">
                                <p style={{ fontWeight: '700' }}>
                                    {t('TemplateSnippet.modal_form_library')}
                                </p>
                                <FormControl
                                    componentClass="select"
                                    value={selectedLibrary}
                                    onChange={e => self.handleSelectLibrary(e)}
                                >
                                    {libraryMap.map(elem => (
                                        <option
                                            value={elem.uuid}
                                            key={elem.name}
                                        >
                                            {elem.name}
                                        </option>
                                    ))}
                                </FormControl>
                            </Col>
                        </Row>

                        {/* Filter for template libraries */}
                        {librarySource === 'template' && (
                            <Row id="modal-form-wrapper" style={{ marginTop: '15px', marginBottom: '15px' }}>
                                <Col id="modal-form">
                                    <Checkbox
                                        checked={filterTextOverlays}
                                        onChange={this.toggleFilterTextOverlays}
                                    >
                                        {t('TemplateSnippetBackgroundPicker.filter_text_overlays')}
                                    </Checkbox>
                                </Col>
                            </Row>
                        )}

                        {selectedLibrary !== '' ? (
                            <Row id="modal-bg-picker">
                                <p style={{ fontWeight: '700' }}>
                                    {t('TemplateSnippet.modal_form_thumbs')}
                                </p>
                                {thumbs.length > 0 ? (
                                    <div className={`modal-bg-thumbnail-group`}>
                                        {thumbs.map((mediaItem) => (
                                            <div
                                                key={mediaItem.uuid}
                                                className={`modal-bg-thumbnail`}
                                                onClick={() => handleSelectBackground(mediaItem.uuid, `${displayOrgUUID}/${selectedLibrary}/${mediaItem.uuid}.${mediaItem.extension}`)}
                                                style={{ position: 'relative' }}
                                            >
                                                <img src={`${config.playersBaseURL}/mediaserver/thumbnails/${displayOrgUUID}/${selectedLibrary}/${mediaItem.uuid}.jpg`} alt={mediaItem.name} />
                                                {mediaItem.hasTextOverlay && (
                                                    <span
                                                        className="label label-success"
                                                        style={{ position: 'absolute', top: '5px', right: '5px', fontSize: '10px' }}
                                                    >
                                                        TEXT
                                                    </span>
                                                )}
                                                <br />
                                                <small>{`${mediaItem.name}`}</small>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                                        {filterTextOverlays
                                            ? t('TemplateSnippetBackgroundPicker.no_text_overlays')
                                            : t('TemplateSnippet.no_media')}
                                    </p>
                                )}
                            </Row>
                        ) : null}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-success"
                        onClick={handleModalSave}
                    >
                        <i className="fas fa-close fa-lg" />
                        <strong>
                            {' '}
                            {t('TemplateSnippet.modal_button_close')}
                        </strong>
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default translate('translations')(TemplateSnippetBackgroundPicker);
